import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Popover,
  Typography,
  Box,
  Paper,
  Stack,
  Divider
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import type { SyncJobHistory, JobStatus, DataSourceType } from '../../types/dataHealth.types';

interface SyncHistoryTableProps {
  history: SyncJobHistory[];
  loading?: boolean;
}

const sourceIcons: Record<DataSourceType, string> = {
  GSC: '🔍',
  BING: '🅱️',
  GA4: '📊',
  CRAWL_LOG: '🕷️'
};

const jobStatusConfig: Record<JobStatus, {
  color: 'success' | 'error' | 'warning' | 'info' | 'default';
  label: string;
}> = {
  RUNNING: { color: 'info', label: 'Running' },
  SUCCESS: { color: 'success', label: 'Success' },
  FAILED: { color: 'error', label: 'Failed' },
  RETRYING: { color: 'warning', label: 'Retrying' }
};

const formatDateTime = (isoString: string): string => {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const calculateDuration = (startedAt: string, completedAt: string | null): string => {
  if (!completedAt) return 'Running...';
  
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationSeconds = Math.floor((end - start) / 1000);
  
  if (durationSeconds < 60) return `${durationSeconds}s`;
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

export const SyncHistoryTable: React.FC<SyncHistoryTableProps> = ({ history }) => {
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedJob, setSelectedJob] = useState<SyncJobHistory | null>(null);

  const handleDetailsClick = (event: React.MouseEvent<HTMLButtonElement>, job: SyncJobHistory) => {
    setDetailsAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleDetailsClose = () => {
    setDetailsAnchorEl(null);
    setSelectedJob(null);
  };

  const detailsPopoverOpen = Boolean(detailsAnchorEl);

  return (
    <Card>
      <CardHeader title="Recent Sync Jobs" />
      <CardContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No sync history available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {history.map((job) => {
                const config = jobStatusConfig[job.status];
                const totalRecords = job.recordsInserted + job.recordsUpdated;
                const errorDetail = job.errorDetail || job.errorDetails;

                return (
                  <TableRow key={job.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {job.jobName || `${job.sourceType} Sync`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{sourceIcons[job.sourceType]}</span>
                        <Typography variant="body2">{job.sourceType}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(job.startedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.durationSeconds !== undefined 
                          ? `${job.durationSeconds}s`
                          : calculateDuration(job.startedAt, job.completedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.label}
                        color={config.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {totalRecords} / {job.recordsFetched}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<InfoIcon />}
                        onClick={(e) => handleDetailsClick(e, job)}
                        color={errorDetail ? 'error' : 'primary'}
                      >
                        {errorDetail ? 'Error' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Popover
          open={detailsPopoverOpen}
          anchorEl={detailsAnchorEl}
          onClose={handleDetailsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Box sx={{ p: 2, minWidth: 400, maxWidth: 500 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Job Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {selectedJob && (
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Job ID</Typography>
                  <Typography variant="body2">{selectedJob.id}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Job Name</Typography>
                  <Typography variant="body2">{selectedJob.jobName || `${selectedJob.sourceType} Sync`}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Source Type</Typography>
                  <Typography variant="body2">{selectedJob.sourceType}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2">{selectedJob.status}</Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Started At</Typography>
                  <Typography variant="body2">{new Date(selectedJob.startedAt).toLocaleString()}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Completed At</Typography>
                  <Typography variant="body2">
                    {selectedJob.completedAt ? new Date(selectedJob.completedAt).toLocaleString() : 'Running...'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body2">
                    {selectedJob.durationSeconds !== undefined 
                      ? `${selectedJob.durationSeconds} seconds`
                      : calculateDuration(selectedJob.startedAt, selectedJob.completedAt)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Records Fetched</Typography>
                  <Typography variant="body2">{selectedJob.recordsFetched}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Records Inserted</Typography>
                  <Typography variant="body2">{selectedJob.recordsInserted}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Records Updated</Typography>
                  <Typography variant="body2">{selectedJob.recordsUpdated}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Retry Count</Typography>
                  <Typography variant="body2">{selectedJob.retryCount || 0}</Typography>
                </Box>
                
                {(selectedJob.errorDetail || selectedJob.errorDetails) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        Error Details
                      </Typography>
                      {selectedJob.errorDetail && (
                        <>
                          <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', color: 'error.main' }}>
                            {selectedJob.errorDetail.error}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {selectedJob.errorDetail.message}
                          </Typography>
                        </>
                      )}
                      {selectedJob.errorDetails && (
                        <>
                          {selectedJob.errorDetails.exception && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', color: 'error.main' }}>
                              {selectedJob.errorDetails.exception}
                            </Typography>
                          )}
                          {selectedJob.errorDetails.message && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {selectedJob.errorDetails.message}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </>
                )}
              </Stack>
            )}
          </Box>
        </Popover>
      </CardContent>
    </Card>
  );
};
